import { GlobalStyles } from "@mui/material";
import Button from "@mui/material/Button";
import Grid2 from "@mui/material/Grid2";
import { makeStyles } from "@mui/styles";
import { globalDefaultColDef } from "@setup/ag-grid/mesf-theme";

import { AgGridReact } from "ag-grid-react";
import type {
	ColDef,
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
	RowClickedEvent,
	RowDragEndEvent,
} from "ag-grid-community";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useFormsContext } from "../../context/FormsContext";
import { LazyLoading } from "@/components/loaders/Loader/LazyLoading";
import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import { ModalGridBox, listMenuColumn } from "../modal-grid";
import { INITIAL_VALUES_QUESTION } from "../../models/QuestionInitialValue";
import { useSearchQuestions } from "../../hooks/useSearchQuestions";
import { updateQuestionSortOrder } from "../../repositories/FormsRepository";
import { NewAndEditQuestionModal } from "./questions-options-modals/modal-new-and-edit-question";
import type { Question } from "../../models/Question";
import { DeleteQuestionDialog } from "./questions-options-modals/modal-delete-question";
import { OptionsTable } from "./optionsTable";
import { useClone } from "../../hooks/useClone";

const useStyles = makeStyles((theme) => ({
	root: {
		"& .ag-row-drag": {
			cursor: "grab !important",
		},
		"& .ag-row-dragging": {
			opacity: 0.6,
			backgroundColor: "#f5f5f5 !important",
		},
		"& .ag-row-drag-move": {
			cursor: "grabbing !important",
		},
		"& .ag-row.drag-target-above::before": {
			content: "''",
			position: "absolute",
			top: "-1px",
			left: "0",
			right: "0",
			height: "2px",
			backgroundColor: theme.palette.primary.main,
			zIndex: 1000,
			boxShadow: `0 0 3px ${theme.palette.primary.main}99`,
		},
		"& .ag-row.drag-target-below::after": {
			content: "''",
			position: "absolute",
			bottom: "-1px",
			left: "0",
			right: "0",
			height: "2px",
			backgroundColor: theme.palette.primary.main,
			zIndex: 1000,
			boxShadow: `0 0 3px ${theme.palette.primary.main}99`,
		},
	},
	modalTitle: {
		backgroundColor: "#ced2cc",
		color: "#495057",
		textAlign: "center",
	},
	modalTitleCloseWO: {
		backgroundColor: "#28a745",
		color: "white",
		textAlign: "center",
	},
	modalWarning: {
		color: "white",
		backgroundColor: "#dc3545",
		textAlign: "center",
	},
	messageTitle: {
		fontSize: 15,
		fontWeight: "bold",
		color: "#0000008A",
	},
	responsive: {
		[theme.breakpoints.up("md")]: {
			padding: "0 10px 0 0 !important",
			marginTop: "0 !important",
		},
	},
	responsiveTwo: {
		[theme.breakpoints.up("md")]: {
			padding: "0 0 0 10px !important",
			marginTop: "0 !important",
		},
	},
	responsiveThree: {
		[theme.breakpoints.up("md")]: {
			padding: "0 !important",
		},
	},
	responsiveFour: {
		[theme.breakpoints.up("md")]: {
			padding: "0 10px 0 0 !important",
		},
	},
	responsiveFive: {
		[theme.breakpoints.up("md")]: {
			padding: "0 0 0 10px !important",
		},
	},
}));

type Props = {
	show: boolean;
	onHide: (onHide: boolean) => void;
};

export const QuestionsAndOptionsTable = ({ show, onHide }: Props) => {
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const classes = useStyles();
	const [columnDefs, setColumnDefs] = useState<ColDef[]>([]);
	const [shouldUpdateWhenExit, setShouldUpdateWhenExit] = useState(false);
	const [newMode, setNewMode] = useState<boolean>(false);
	const [questionIdToSearchOptions, setQuestionIdToSearchOptions] = useState<
		number | null
	>(null);

	const [openNewEditQuestion, setOpenNewEditQuestion] = useState(false);
	const [openDeleteQuestion, setOpenDeleteQuestion] = useState(false);
	const [openNewEditOption, setOpenNewEditOption] = useState(false);
	const [openDeleteOption, setOpenDeleteOption] = useState(false);
	const [newOptionMode, setNewOptionMode] = useState<boolean>(false);

	const {
		state: {
			SectionName,
			questionList,
			questionOptionsList,
			questionSelectedInTable,
		},
		actions: {
			setQuestionSelectedInTable,
			setQuestionId,
			setQuestionText,
			setQuestionOptionsList,
		},
	} = useFormsContext();
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [error, setError] = useState<string>("");

	const searchQuestions = useSearchQuestions({ setIsLoading, setError });
	const { CloneQuestionAndOptions } = useClone();

	const handleClone = (questionId: number) => {
		CloneQuestionAndOptions({ QuestionId: questionId, searchQuestions });
		setShouldUpdateWhenExit(true);
	};

	const handleNewQuestion = () => {
		setQuestionSelectedInTable(INITIAL_VALUES_QUESTION);
		setQuestionId(null);
		setQuestionText("");
		setNewMode(true);
		setOpenNewEditQuestion(true);
	};

	const loadingOverlayComponent = useMemo<any>(() => {
		return LazyLoading;
	}, []);

	const rowClicked = (event: RowClickedEvent) => {
		if (event.data) setQuestionIdToSearchOptions(event.data.QuestionId);
	};

	const onRowDragEnd = async (params: RowDragEndEvent) => {
		if (!params.node?.data?.QuestionId || !params.api) {
			const allRows = document.querySelectorAll(".ag-row");
			allRows.forEach((row) => {
				row.classList.remove("drag-target-above", "drag-target-below");
			});
			return;
		}

		let questionIdBelow = null;
		let isDroppingAbove = false;
		let isDroppingBelow = false;

		// Check if we have a target node to determine drop position
		if (params.overNode && params.overNode.data) {
			const targetRowElement = document.querySelector(
				`[row-id="${params.overNode.id}"]`,
			);

			if (targetRowElement) {
				// Read the classes BEFORE cleaning them up
				isDroppingAbove =
					targetRowElement.classList.contains("drag-target-above");
				isDroppingBelow =
					targetRowElement.classList.contains("drag-target-below");

				if (isDroppingAbove) {
					// Insert BEFORE the target row - SP expects the question to insert BEFORE
					questionIdBelow = params.overNode.data.QuestionId;
				} else if (isDroppingBelow) {
					// Insert AFTER the target row - find the next row to insert BEFORE
					const targetIndex = params.overNode.rowIndex!;
					const nextRow = params.api.getDisplayedRowAtIndex(targetIndex + 1);
					questionIdBelow =
						nextRow && nextRow.data ? nextRow.data.QuestionId : null;
				} else {
					// Fallback - treat as dropping above
					questionIdBelow = params.overNode.data.QuestionId;
				}
			}
		}

		const allRows = document.querySelectorAll(".ag-row");
		allRows.forEach((row) => {
			row.classList.remove("drag-target-above", "drag-target-below");
		});

		try {
			const res = await updateQuestionSortOrder(
				params.node.data.QuestionId,
				questionIdBelow,
			);

			if (res.ok) {
				searchQuestions();
				setShouldUpdateWhenExit(true);
			} else {
				setError(res.message || "Error updating question order");
			}
		} catch (error) {
			setError("Error updating question priority");
		}
	};

	const handleCancel = () => {
		onHide(shouldUpdateWhenExit);
	};

	const getQuestionContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const data: Question = params.node?.data;
			const result: (DefaultMenuItem | MenuItemDef)[] = [];

			if (data) {
				result.push({
					name: "Edit",
					action: () => {
						setNewMode(false);
						setQuestionSelectedInTable(data);
						setQuestionId(data.QuestionId);
						setQuestionText(data.QuestionText);
						setOpenNewEditQuestion(true);
					},
					icon: '<i class="fas fa-edit"></i>',
				});
			}

			if (data) {
				result.push({
					name: "New Question",
					action: () => {
						setQuestionSelectedInTable(INITIAL_VALUES_QUESTION);
						setQuestionId(null);
						setQuestionText("");
						setNewMode(true);
						setOpenNewEditQuestion(true);
					},
					icon: '<i class="fas fa-plus"></i>',
				});
			}

			if (data) {
				result.push({
					name: "New Option",
					action: () => {
						setNewOptionMode(true);
						setQuestionId(null);
						setQuestionText("");
						setQuestionSelectedInTable(INITIAL_VALUES_QUESTION);
						setQuestionIdToSearchOptions(data?.QuestionId);
						setOpenNewEditOption(true);
					},
					icon: '<i class="fas fa-plus"></i>',
				});
			}

			if (data) {
				result.push({
					name: "Duplicate Question",
					action: () => {
						setQuestionSelectedInTable(data);
						handleClone(data.QuestionId as number);
					},
					icon: '<i class="fas fa-clone"></i>',
				});
			}

			if (data) {
				result.push({
					name: "Delete",
					action: () => {
						setNewMode(false);
						setOpenDeleteQuestion(true);
						setQuestionSelectedInTable(data);
					},
					icon: '<i class="fas fa-trash"></i>',
				});
			}

			const addNewQuestion: (DefaultMenuItem | MenuItemDef)[] = [];

			if (!data) {
				addNewQuestion.push({
					name: "New Question",
					action: () => {
						setQuestionSelectedInTable(INITIAL_VALUES_QUESTION);
						setNewMode(true);
						setQuestionId(null);
						setQuestionText("");
						setOpenNewEditQuestion(true);
					},
					icon: '<i class="fas fa-plus"></i>',
				});
			}

			return data ? result : addNewQuestion;
		},
		[questionList],
	);

	useEffect(() => {
		if (show) {
			searchQuestions();
			setShouldUpdateWhenExit(false);
			setColumnDefs([]);
			setQuestionOptionsList([]);
			setQuestionIdToSearchOptions(null);

			const newColumns: ColDef[] = [];

			newColumns.push({
				field: "",
				headerName: "",
				rowDrag: true,
				minWidth: 50,
				maxWidth: 50,
				suppressHeaderMenuButton: true,
				suppressHeaderFilterButton: true,
			});

			newColumns.push({
				field: "QuestionText",
				headerName: "Question Name",
				sortable: true,
				flex: 4,
				minWidth: 130,
			});
			newColumns.push({
				field: "IncludesComment",
				headerName: "Includes Comment",
				sortable: false,
				flex: 1,
				minWidth: 130,
				valueFormatter: (params) => (params.value ? "Yes" : "No"),
			}),
				newColumns.push({
					field: "IsRequired",
					headerName: "Required",
					sortable: false,
					flex: 1,
					minWidth: 130,
					valueFormatter: (params) => (params.value ? "Yes" : "No"),
				});
			newColumns.push(listMenuColumn);

			setColumnDefs(newColumns);
		}
	}, [show]);

	useEffect(() => {
		if (isLoading) {
			gridApi?.showLoadingOverlay();
		} else gridApi?.hideOverlay();
	}, [isLoading, questionList, gridApi]);

	const gridHeight = "55vh";

	return (
		<>
			<GlobalStyles
				styles={{
					".MuiDialog-paper[aria-labelledby='form-questions-options-modal']": {
						width: "100%",
					},
				}}
			/>
			<MesfModal
				title={
					SectionName
						? `QUESTIONS AND OPTIONS - ${SectionName}`
						: "QUESTIONS AND OPTIONS"
				}
				open={show}
				handleClose={handleCancel}
				maxWidth="lg"
				id="form-questions-options-modal"
			>
				<MesfModal.Content dividers>
					<Grid2 container spacing={2}>
						<Grid2 size={{ md: 6, xs: 12 }}>
							<ModalGridBox height={gridHeight} onAdd={handleNewQuestion}>
								<div
									style={{ height: "100%", width: "100%" }}
									className={classes.root}
								>
									<AgGridReact
										rowData={questionList}
										columnDefs={columnDefs}
										defaultColDef={globalDefaultColDef}
										rowHeight={38}
										headerHeight={42}
										loadingOverlayComponent={loadingOverlayComponent}
										animateRows={true}
										getContextMenuItems={(e: any) =>
											getQuestionContextMenuItems(e)
										}
										pagination={false}
										onRowClicked={rowClicked}
										onRowDoubleClicked={(event) => {
											setNewMode(false);
											setQuestionSelectedInTable(event.data);
											setQuestionId(event.data?.QuestionId);
											setQuestionText(event.data?.QuestionText);
											setOpenNewEditQuestion(true);
										}}
										rowSelection="single"
										onGridReady={(params) => setGridApi(params.api)}
										getRowId={(params) => params.data.QuestionId}
										onRowDragEnd={onRowDragEnd}
										rowDragEntireRow={true}
										suppressMoveWhenRowDragging={false}
										suppressRowDrag={false}
										onRowDragMove={(params) => {
											const allRows = document.querySelectorAll(".ag-row");
											allRows.forEach((row) => {
												row.classList.remove(
													"drag-target-above",
													"drag-target-below",
												);
											});

											if (params.overNode) {
												const targetRowElement = document.querySelector(
													`[row-id="${params.overNode.id}"]`,
												);
												if (targetRowElement) {
													const rect = targetRowElement.getBoundingClientRect();
													const mouseY = params.event?.clientY || 0;
													const rowMiddle = rect.top + rect.height / 2;

													if (mouseY < rowMiddle) {
														targetRowElement.classList.add("drag-target-above");
													} else {
														targetRowElement.classList.add("drag-target-below");
													}
												}
											}
										}}
										onRowDragLeave={() => {
											const allRows = document.querySelectorAll(".ag-row");
											allRows.forEach((row) => {
												row.classList.remove(
													"drag-target-above",
													"drag-target-below",
												);
											});
										}}
									/>
								</div>
							</ModalGridBox>
						</Grid2>
						<Grid2 size={{ md: 6, xs: 12 }}>
							<OptionsTable
								questionIdToSearchOptions={questionIdToSearchOptions as number}
								openNewEditOption={openNewEditOption}
								openDeleteOption={openDeleteOption}
								setOpenNewEditOption={setOpenNewEditOption}
								setOpenDeleteOption={setOpenDeleteOption}
								setNewOptionMode={setNewOptionMode}
								newOptionMode={newOptionMode}
								setShouldUpdateWhenExit={setShouldUpdateWhenExit}
								gridHeight={gridHeight}
							/>
						</Grid2>
					</Grid2>
				</MesfModal.Content>
				<MesfModal.Actions style={{ padding: "20px 16px 16px" }}>
					<Grid2 container spacing={2} justifyContent="flex-end" width="100%">
						<Grid2 size={{ md: 3, xs: 12 }}>
							<Button
								fullWidth
								variant="contained"
								color="inherit"
								onClick={handleCancel}
							>
								Close
							</Button>
						</Grid2>
					</Grid2>
				</MesfModal.Actions>
			</MesfModal>
			<NewAndEditQuestionModal
				show={openNewEditQuestion}
				data={questionSelectedInTable}
				newMode={newMode}
				setShouldUpdateWhenExit={setShouldUpdateWhenExit}
				onHide={(shouldUpdate: boolean) => {
					if (shouldUpdate) {
						searchQuestions();
						setQuestionIdToSearchOptions(null);
						setQuestionOptionsList([]);
					}
					setOpenNewEditQuestion(false);
				}}
			/>
			<DeleteQuestionDialog
				show={openDeleteQuestion}
				setShouldUpdateWhenExit={setShouldUpdateWhenExit}
				onHide={(shouldUpdate: boolean) => {
					if (shouldUpdate) {
						setQuestionIdToSearchOptions(null);
						searchQuestions();
						setQuestionOptionsList([]);
					}
					setOpenDeleteQuestion(false);
				}}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
